import { Component, OnInit, OnDestroy } from '@angular/core';
import { BarcodeFormat } from '@zxing/library';
import { ActivatedRoute, Router } from '@angular/router';
import { QrPaymentService } from '../../../../services/qr-payment.service';
import { AuthService } from 'src/app/pages/auth/abstraction/auth.service';
import { ONBOARDING_FLOW } from 'src/app/pages/auth/models/Onboarding';

@Component({
  selector: 'app-qr-reader',
  templateUrl: './qr-reader.component.html',
  styleUrl: './qr-reader.component.scss',
})
export class QrReaderComponent implements OnInit, OnDestroy {
  scannerEnabled = false;
  allowedFormats = [BarcodeFormat.QR_CODE];
  hasPermission = false;
  hasDevices = false;
  currentDevice: MediaDeviceInfo | null = null;
  availableDevices: MediaDeviceInfo[] = [];
  cameraStarted = false;
  showStartButton = true;

  get isInIframe(): boolean {
    return window.self !== window.top;
  }

  constructor(
    private router: Router,
    private qrPaymentService: QrPaymentService,
    private authSvc: AuthService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.checkPermissions();

    this.route.queryParams.subscribe((params) => {
      if (params['signature']) {
        this.authSvc.patchOnboardingState({
          flow: ONBOARDING_FLOW.WIDGET,
          signature: params['signature'],
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.scannerEnabled = false;
  }

  onCamerasFound(devices: MediaDeviceInfo[]): void {
    this.availableDevices = devices;
    this.hasDevices = devices && devices.length > 0;

    if (devices && devices.length > 0) {
      // Filter to videoinput devices only
      const videoDevices = devices.filter((device) => device.kind === 'videoinput');

      if (videoDevices.length > 0) {
        // Find back camera using multiple strategies
        const backCamera = this.findBackCamera(videoDevices);
        this.currentDevice = backCamera || videoDevices[0];
      } else {
        this.currentDevice = devices[0];
      }

      // Only enable scanner if user has clicked start button
      if (this.cameraStarted) {
        this.scannerEnabled = true;
      }
    }
  }

  /**
   * Attempts to find the back/rear camera using multiple detection strategies
   */
  private findBackCamera(devices: MediaDeviceInfo[]): MediaDeviceInfo | null {
    if (devices.length === 0) return null;
    if (devices.length === 1) return devices[0];

    const labelLower = (label: string) => label.toLowerCase();

    // Strategy 1: Check for explicit back/rear camera labels
    const backCameraByLabel = devices.find((device) => {
      const label = labelLower(device.label);
      return (
        label.includes('back') ||
        label.includes('rear') ||
        label.includes('environment') || // Standard facingMode name
        label.includes('facing back') ||
        (label.includes('camera') && !label.includes('front') && !label.includes('user'))
      );
    });
    if (backCameraByLabel) return backCameraByLabel;

    // Strategy 2: Exclude front cameras and pick from remaining
    const nonFrontCameras = devices.filter((device) => {
      const label = labelLower(device.label);
      return !(
        label.includes('front') ||
        label.includes('user') ||
        label.includes('selfie') ||
        label.includes('facing user') ||
        label.includes('facing front')
      );
    });
    if (nonFrontCameras.length > 0) {
      // If we have non-front cameras, prefer the last one (often back camera)
      return nonFrontCameras[nonFrontCameras.length - 1];
    }

    // Strategy 3: On mobile devices, back camera is often the second camera
    // or has a longer deviceId (more complex identifier)
    if (devices.length >= 2) {
      // Prefer the device with longer deviceId (often more specific = back camera)
      const sortedByDeviceIdLength = [...devices].sort(
        (a, b) => b.deviceId.length - a.deviceId.length,
      );
      return sortedByDeviceIdLength[0];
    }

    // Fallback: return null to use first device
    return null;
  }

  onDeviceSelectChange(deviceId: string): void {
    const device = this.availableDevices.find((d) => d.deviceId === deviceId);
    if (device) {
      this.currentDevice = device;
    }
  }

  onScanSuccess(result: string): void {
    // Parse and set QR data in service
    this.qrPaymentService.parseAndSetQrData(result);
    // Stop scanning after successful read
    this.scannerEnabled = false;
    // Navigate to payment confirm page
    this.router.navigate(['/payment-confirm']);
  }

  onScanError(error: Error): void {
    console.error('QR scan error:', error);
  }

  onPermissionResponse(hasPermission: boolean): void {
    this.hasPermission = hasPermission;
    if (hasPermission && this.cameraStarted) {
      // Only enable scanner if user has clicked start button
      this.scannerEnabled = true;
    } else if (!hasPermission) {
      // If permission denied and we're in an iframe, log a helpful message
      if (window.self !== window.top) {
        console.warn('Camera permission denied. Ensure the iframe has allow="camera" attribute.');
      }
      // Show start button again if permission was denied
      this.showStartButton = true;
      this.cameraStarted = false;
    }
  }

  checkPermissions(): void {
    // Don't auto-enable scanner - wait for user interaction
    // This is required for camera access in iframes
    this.scannerEnabled = false;
  }

  startScanning(): void {
    // This method is called on user click to start the camera
    // User interaction is required for camera access in iframes
    setTimeout(() => {
      this.cameraStarted = true;
      this.showStartButton = false;
      this.scannerEnabled = true;
    }, 500);
  }

  stopScanning(): void {
    this.scannerEnabled = false;
  }
}
